import type { SourceKind, TrackFinishRequest } from '../../../shared/protocol.ts';
import { api, ApiError } from './api.ts';
import { Observable, sleep } from './emitter.ts';
import { isJournalName, type JournalData } from './journal.ts';
import { fragmentEndTime } from './mp4.ts';
import type { LocalStorageFiles } from './storage.ts';

/**
 * Background uploader for local recordings.
 *
 * - Resumable: every job is persisted, and the server tells us how much it already has.
 * - Incremental: files upload while they are still being recorded, so most of a take is
 *   already on the server when the host presses stop.
 * - Polite: the rate comes from the BandwidthGovernor, which backs off as soon as the live
 *   call shows signs of congestion. Uploads can always wait; the call cannot.
 * - Verified: each chunk carries a SHA-256 the server checks before accepting it.
 */

/** Saved periodically while recording, so a file orphaned by a crash can still be repaired and placed. */
export interface RecoveryDraft {
  info: Omit<TrackFinishRequest, 'size'>;
  format: 'mp4' | 'wav';
  /** End of the MP4 init segment / WAV header. */
  initEnd: number;
  /** A byte offset known to end on a complete MP4 fragment (or the WAV size so far). */
  safeEnd: number;
  blockAlign?: number;
}

export interface UploadJob {
  trackId: string;
  uploadToken: string;
  /** Set when chunks go to the file service rather than this server (same protocol). */
  uploadUrl?: string;
  /** A keep-local take: only the file's timings are sent, until the person chooses to upload it (uploadKept). */
  keepLocal?: boolean;
  localName: string;
  studioId: string;
  takeId: string;
  takeNumber: number;
  label: string;
  kind: SourceKind;
  participantName: string;
  /** Final local size once recording of this file is finished. */
  size: number | null;
  uploaded: number;
  /** Byte ranges rewritten after they were uploaded (e.g. WAV header patched at the end). */
  dirty: [number, number][];
  finish: TrackFinishRequest | null;
  finishSent: boolean;
  done: boolean;
  error: string | null;
  createdAt: number;
  completedAt?: number;
  draft?: RecoveryDraft;
  recovered?: boolean;
}

const KEY = 'takehouse.uploads.v1';
const POLICY_KEY = 'takehouse.localCopies';

/** What happens to the local copy once the server has confirmed it holds the whole file. */
export type LocalCopyPolicy = 'keep' | 'delete-when-uploaded' | 'delete-after-7-days';
const SEVEN_DAYS = 7 * 24 * 3600 * 1000;
const MAX_CHUNK = 8 * 1024 * 1024;
const MIN_CHUNK = 256 * 1024;
const KIND_ORDER: Record<SourceKind, number> = { microphone: 0, 'system-audio': 1, camera: 2, screen: 3 };

export interface RateSource {
  /** Bits per second the uploader may use right now. Infinity = unlimited, 0 = hold. */
  allowedBps(): number;
  reportUpload(bytes: number, ms: number): void;
}

export class Uploader extends Observable {
  jobs: UploadJob[] = [];
  /** Bytes currently on local disk per job, while still being recorded. */
  private localSize = new Map<string, number>();
  private running = false;
  private wake: (() => void) | null = null;
  private backoffMs = 0;
  online = navigator.onLine;
  localCopyPolicy: LocalCopyPolicy = (localStorage.getItem(POLICY_KEY) as LocalCopyPolicy) || 'keep';
  /**
   * Average sending rate over the last few seconds, pauses included: what the connection actually carries.
   * (A single chunk's rate is a burst, often far above the paced average the governor allows.)
   */
  currentBps = 0;
  private sent: { start: number; bytes: number }[] = [];
  lastError: string | null = null;
  private storage: LocalStorageFiles;
  private rate: RateSource;

  constructor(storage: LocalStorageFiles, rate: RateSource) {
    super();
    this.storage = storage;
    this.rate = rate;
    this.load();
    addEventListener('online', () => {
      this.online = true;
      this.backoffMs = 0;
      this.kick();
    });
    addEventListener('offline', () => {
      this.online = false;
      this.changed();
    });
    // Resume anything left over from a previous visit or a crash.
    // Always look once: journals can hold files this browser has no upload record of.
    setTimeout(() => this.kick(), 500);
  }

  private load() {
    try {
      this.jobs = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    } catch {
      this.jobs = [];
    }
  }

  /** Jobs this tab removed on purpose, so a merge doesn't bring them back. */
  private forgotten = new Set<string>();

  /**
   * Another tab of this browser keeps its own list in memory and saves it too. Merging (this tab's jobs, plus
   * any the stored list has that this tab doesn't know) stops either tab erasing the other's records.
   */
  private save() {
    try {
      const stored = JSON.parse(localStorage.getItem(KEY) ?? '[]') as UploadJob[];
      const mine = new Set(this.jobs.map((j) => j.trackId));
      const others = stored.filter((j) => !mine.has(j.trackId) && !this.forgotten.has(j.trackId));
      localStorage.setItem(KEY, JSON.stringify([...others, ...this.jobs]));
    } catch {}
    this.changed();
  }

  add(job: Omit<UploadJob, 'uploaded' | 'dirty' | 'finish' | 'finishSent' | 'done' | 'error' | 'createdAt' | 'size'>) {
    this.jobs.push({ ...job, size: null, uploaded: 0, dirty: [], finish: null, finishSent: false, done: false, error: null, createdAt: Date.now() });
    // The file may have been growing before the server registered it.
    if (!this.localSize.has(job.localName)) {
      void this.storage.size(job.localName).then((n) => {
        this.localSize.set(job.localName, Math.max(n, this.localSize.get(job.localName) ?? 0));
        this.kick();
      });
    }
    this.save();
    this.kick();
  }

  /** Called by the recorder for every write to local storage. */
  noteWrite(localName: string, position: number, length: number) {
    const end = position + length;
    this.localSize.set(localName, Math.max(this.localSize.get(localName) ?? 0, end));
    const job = this.jobs.find((j) => j.localName === localName);
    if (job?.done && position < job.uploaded) {
      // The server repairs WAV headers itself (server/src/index.ts, repairWavHeader); anything else is worth knowing.
      console.warn(`[takehouse] ${job.label}: ${length} bytes at ${position} were written after its upload finished`);
    } else if (job && position < job.uploaded) {
      job.dirty.push([position, Math.min(end, job.uploaded)]);
      this.save();
    }
    this.kick();
  }

  setDraft(localName: string, draft: RecoveryDraft) {
    const job = this.jobs.find((j) => j.localName === localName);
    if (!job || job.size != null) return;
    job.draft = draft;
    this.save();
  }

  /**
   * Files the server never registered (it was unreachable when they started, or this browser lost its upload
   * records) are still listed in their take journals. Register them now, so they are repaired and uploaded like
   * any other. Registration is idempotent, so a file the server already has is reclaimed, not duplicated.
   */
  async recoverJournals() {
    let present: Set<string>;
    try {
      present = new Set((await this.storage.list()).map((f) => f.name));
    } catch {
      return;
    }
    for (const name of [...present].filter(isJournalName)) {
      let data: JournalData;
      try {
        data = JSON.parse(await this.storage.readText(name));
      } catch {
        continue;
      }
      if (data?.format !== 'takehouse-journal@1') continue;
      let changed = false;
      for (const f of data.files) {
        if (!present.has(f.localName) || this.jobs.some((j) => j.localName === f.localName)) continue;
        // A recorder in this or another tab still holds the file: that tab registers it itself.
        const free = navigator.locks
          ? await navigator.locks.request(`takehouse:${f.localName}`, { ifAvailable: true }, async (lock) => !!lock)
          : true;
        if (!free) continue;
        try {
          const res = await api.registerTrack(data.key, {
            takeId: data.takeId,
            participantId: data.participantId,
            participantName: data.participantName,
            sourceId: f.sourceId,
            sourceKind: f.kind,
            label: f.label,
            ext: f.ext,
            mime: f.ext === 'wav' ? 'audio/wav' : 'video/mp4',
            clientFileId: f.localName,
          });
          const already = await api.uploadStatus(res.trackId, res.uploadToken).catch(() => ({ received: 0 }));
          const job: UploadJob = {
            trackId: res.trackId,
            uploadToken: res.uploadToken,
            uploadUrl: res.uploadUrl,
            keepLocal: res.keepLocal,
            localName: f.localName,
            studioId: data.studioId,
            takeId: data.takeId,
            takeNumber: data.takeNumber,
            label: f.label,
            kind: f.kind,
            participantName: data.participantName,
            size: null,
            uploaded: already.received,
            dirty: [],
            finish: null,
            finishSent: false,
            done: false,
            error: null,
            createdAt: Date.now(),
            draft: f.draft,
          };
          // Closed properly: its manifest is in the journal. Otherwise recoverOrphans() repairs it next.
          if (f.state !== 'recording' && f.finish && f.size != null) {
            job.size = f.size;
            job.finish = { ...f.finish, size: f.size };
          }
          this.jobs.push(job);
          f.trackId = res.trackId;
          changed = true;
          console.info(`[takehouse] ${f.label} (take ${data.takeNumber}) found in its journal; registered and queued for upload`);
        } catch (e) {
          // Server unreachable: the journal keeps it for the next visit.
          console.warn(`[takehouse] couldn't register ${f.label} from its journal: ${(e as Error).message}`);
        }
      }
      if (changed) {
        this.save();
        await this.storage.put(name, new TextEncoder().encode(JSON.stringify(data))).catch(() => {});
      }
    }
  }

  /**
   * Finds files whose recorder died (crash, power cut, tab killed): no finished size and nobody
   * holding their lock. Repairs them so they're valid, then lets them upload like any other.
   */
  async recoverOrphans() {
    for (const job of this.jobs.filter((j) => j.size == null && !j.done)) {
      const free = navigator.locks
        ? await navigator.locks.request(`takehouse:${job.localName}`, { ifAvailable: true }, async (lock) => !!lock)
        : true;
      if (!free) continue; // a recorder in this or another tab is still writing it
      try {
        const actual = await this.storage.size(job.localName);
        const d = job.draft;
        let size: number;
        let durationMs: number | undefined;
        if (job.localName.endsWith('.wav')) {
          const header = d?.initEnd || (await wavDataStart(this.storage, job.localName));
          const block = d?.blockAlign ?? 2;
          size = header + Math.floor(Math.max(0, actual - header) / block) * block;
          // The header still says "0 bytes of audio" – fix the RIFF and data chunk sizes.
          const riff = new Uint8Array(4);
          new DataView(riff.buffer).setUint32(0, size - 8, true);
          const data = new Uint8Array(4);
          new DataView(data.buffer).setUint32(0, size - header, true);
          await this.storage.write(job.localName, 4, riff);
          await this.storage.write(job.localName, header - 4, data);
          this.noteWrite(job.localName, 4, 4);
          this.noteWrite(job.localName, header - 4, 4);
          if (d?.info.sampleRate) durationMs = ((size - header) / block / d.info.sampleRate) * 1000;
        } else {
          // Keep only complete fragments; a half-written one at the end would confuse decoders.
          let walk = await lastCompleteFragmentEnd(this.storage, job.localName, d?.safeEnd ?? 0, actual);
          // Nothing after the last saved checkpoint: scan from the start, so the duration comes from the
          // file's real last fragment rather than from a recovery note that may be seconds old.
          if (!walk.lastMoof && d?.initEnd) walk = await lastCompleteFragmentEnd(this.storage, job.localName, d.initEnd, actual);
          size = walk.end;
          if (walk.lastMoof && d?.initEnd && d.info.frameRate) {
            const init = await this.storage.read(job.localName, 0, d.initEnd);
            const moof = await this.storage.read(job.localName, walk.lastMoof[0], walk.lastMoof[1]);
            const end = fragmentEndTime(init, moof, d.info.frameRate);
            if (end != null) durationMs = end * 1000;
          }
        }
        const fallback: Omit<TrackFinishRequest, 'size'> = { anchorServerMs: NaN, anchorUncertaintyMs: NaN, durationMs: NaN };
        job.recovered = true;
        this.finish(job.localName, size, {
          ...(d?.info ?? fallback),
          // The draft can be a few seconds stale; trust what's actually in the repaired file.
          durationMs: durationMs != null ? Math.round(durationMs) : (d?.info.durationMs ?? NaN),
          endedReason: 'error',
        });
      } catch (e) {
        job.error = `Recovery failed: ${(e as Error).message}`;
        this.save();
      }
    }
  }

  /** The local file is complete: upload the rest, then send the timing manifest. */
  finish(localName: string, size: number, info: Omit<TrackFinishRequest, 'size'>) {
    const job = this.jobs.find((j) => j.localName === localName);
    if (!job) return;
    job.size = size;
    job.finish = { ...info, size };
    this.localSize.set(localName, size);
    this.save();
    this.kick();
  }

  /** Files still to send: kept-local files count only until their timings are sent. */
  get pending() {
    return this.jobs.filter((j) => !j.done && !(j.keepLocal && j.finishSent));
  }

  /** Files of keep-local takes that stay on this computer. */
  get kept() {
    return this.jobs.filter((j) => j.keepLocal && !j.done);
  }

  /** Stops tracking these files (they were removed from this computer). */
  forget(localNames: string[]) {
    const drop = new Set(localNames);
    for (const j of this.jobs) if (drop.has(j.localName)) this.forgotten.add(j.trackId);
    this.jobs = this.jobs.filter((j) => !drop.has(j.localName));
    this.save();
    this.changed();
  }

  /** Uploads kept-local files after all (a take's, or every one). */
  uploadKept(takeId?: string) {
    for (const j of this.kept) if (!takeId || j.takeId === takeId) j.keepLocal = false;
    this.save();
    this.kick();
  }

  totals(jobs = this.jobs) {
    let total = 0;
    let uploaded = 0;
    for (const j of jobs) {
      const size = j.size ?? this.localSize.get(j.localName) ?? j.uploaded;
      total += size;
      uploaded += Math.min(j.uploaded, size);
    }
    return { total, uploaded };
  }

  kick() {
    this.wake?.();
    if (!this.running) void this.run();
  }

  private recovered = false;

  private async run() {
    this.running = true;
    try {
      if (!this.recovered) {
        this.recovered = true;
        await this.recoverJournals();
        await this.recoverOrphans();
        await this.applyLocalCopyPolicy();
      }
      // After a reload, local sizes are unknown until we ask storage.
      for (const j of this.pending) {
        if (!this.localSize.has(j.localName)) this.localSize.set(j.localName, await this.storage.size(j.localName).catch(() => 0));
      }
      while (true) {
        const next = this.nextWork();
        if (!next) {
          this.currentBps = 0;
          this.sent = [];
          this.changed();
          await this.idle(5000);
          if (!this.nextWork()) {
            if (!this.pending.length) break;
            continue;
          }
          continue;
        }
        const allowed = this.rate.allowedBps();
        if (allowed <= 0 || !this.online) {
          this.currentBps = 0;
          this.sent = [];
          this.changed();
          await this.idle(2000);
          continue;
        }
        if (this.backoffMs) {
          await this.idle(this.backoffMs);
        }
        await this.step(next, allowed);
      }
    } finally {
      this.running = false;
    }
  }

  private idle(ms: number) {
    return new Promise<void>((resolve) => {
      const t = setTimeout(done, ms);
      function done() {
        clearTimeout(t);
        resolve();
      }
      this.wake = () => {
        this.wake = null;
        done();
      };
    });
  }

  private nextWork(): { job: UploadJob; start: number; end: number; patch: boolean } | { job: UploadJob; finishOnly: true } | null {
    const order = [...this.pending].sort(
      (a, b) => a.takeNumber - b.takeNumber || KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.createdAt - b.createdAt,
    );
    for (const job of order) {
      if (job.error === 'gone') continue;
      // Kept on this computer: only the timings go, so the take's timeline still works.
      if (job.keepLocal) {
        if (job.finish && !job.finishSent) return { job, finishOnly: true };
        continue;
      }
      // Rewrites of already-uploaded bytes (e.g. the WAV header) go first, so by the time the server
      // can consider a file complete – and copy it to long-term storage – every byte is final.
      if (job.dirty.length) {
        const [s, e] = job.dirty[0];
        return { job, start: s, end: Math.min(e, s + MAX_CHUNK), patch: true };
      }
      // Then the final size + timing, as soon as we know it, so the host sees real progress.
      if (job.finish && !job.finishSent) return { job, finishOnly: true };
      const local = job.size ?? this.localSize.get(job.localName) ?? 0;
      const available = local - job.uploaded;
      // While recording, wait for a reasonable chunk; once finished, send whatever is left.
      if (available >= MIN_CHUNK || (job.size != null && available > 0)) {
        return { job, start: job.uploaded, end: job.uploaded + Math.min(available, MAX_CHUNK), patch: false };
      }
    }
    return null;
  }

  private noteSent(start: number, bytes: number) {
    const now = performance.now();
    this.sent.push({ start, bytes });
    this.sent = this.sent.filter((x) => now - x.start < 5000);
    const span = Math.max(now - this.sent[0].start, 1000) / 1000;
    this.currentBps = (this.sent.reduce((a, x) => a + x.bytes, 0) * 8) / span;
  }

  private async step(work: NonNullable<ReturnType<Uploader['nextWork']>>, allowedBps: number) {
    const { job } = work;
    try {
      if ('finishOnly' in work) {
        const info = await api.finishTrack(job.trackId, job.uploadToken, job.finish!);
        job.finishSent = true;
        job.uploaded = info.receivedBytes;
        this.markIfDone(job);
        this.save();
        return;
      }
      // Smaller chunks when throttled so the rate limit is smooth rather than bursty.
      const budget = Number.isFinite(allowedBps) ? Math.max(MIN_CHUNK / 4, (allowedBps / 8) * 2) : MAX_CHUNK;
      const end = Math.min(work.end, work.start + budget);
      const data = await this.storage.read(job.localName, work.start, end);
      if (!data.length) {
        await sleep(500);
        return;
      }
      const hash = await sha256(data);
      const t0 = performance.now();
      const res = await fetch(`${job.uploadUrl ?? `/api/tracks/${job.trackId}/data`}?offset=${work.start}`, {
        method: 'PUT',
        headers: { 'x-upload-token': job.uploadToken, 'x-sha256': hash, 'content-type': 'application/octet-stream' },
        body: data,
      });
      const body = await res.json().catch(() => ({}));
      const ms = performance.now() - t0;
      if (res.status === 409 && body.archived) {
        // The server already holds the finished file in long-term storage; nothing left to send.
        job.dirty = [];
        job.uploaded = body.received;
        this.markIfDone(job);
      } else if (res.status === 409 && typeof body.received === 'number') {
        job.uploaded = body.received;
      } else if (res.status === 404 || res.status === 403) {
        job.error = 'gone';
        this.lastError = `Server no longer accepts “${job.label}” (${body.error ?? res.status}).`;
      } else if (!res.ok) {
        throw new ApiError(res.status, body);
      } else {
        if (work.patch) {
          job.dirty[0][0] = end;
          if (job.dirty[0][0] >= job.dirty[0][1]) job.dirty.shift();
        } else {
          job.uploaded = body.received;
        }
        this.rate.reportUpload(data.length, ms);
        this.noteSent(t0, data.length);
        this.backoffMs = 0;
        this.lastError = null;
        job.error = null;
        this.markIfDone(job);
        // Pace to the allowed rate: a chunk of N bits at R bps "costs" N/R seconds.
        if (Number.isFinite(allowedBps)) {
          const target = ((data.length * 8) / allowedBps) * 1000;
          if (target > ms) await this.idle(target - ms);
        }
      }
      this.save();
    } catch (e) {
      this.backoffMs = Math.min(60_000, Math.max(2000, this.backoffMs * 2));
      this.lastError = navigator.onLine ? `Upload paused, retrying: ${(e as Error).message}` : 'Offline – uploads will resume automatically.';
      job.error = (e as Error).message;
      this.currentBps = 0;
      this.sent = [];
      this.changed();
    }
  }

  private markIfDone(job: UploadJob) {
    if (job.finishSent && job.size != null && job.uploaded >= job.size && !job.dirty.length) {
      job.done = true;
      job.completedAt = Date.now();
      if (this.localCopyPolicy === 'delete-when-uploaded') void this.applyLocalCopyPolicy();
    }
  }

  setLocalCopyPolicy(policy: LocalCopyPolicy) {
    this.localCopyPolicy = policy;
    try {
      localStorage.setItem(POLICY_KEY, policy);
    } catch {}
    void this.applyLocalCopyPolicy();
    this.changed();
  }

  /** Only ever deletes files the server has confirmed it holds in full (size matched, every chunk checksummed). */
  async applyLocalCopyPolicy() {
    if (this.localCopyPolicy === 'delete-when-uploaded') await this.clearUploaded(0);
    else if (this.localCopyPolicy === 'delete-after-7-days') await this.clearUploaded(SEVEN_DAYS);
  }

  /** Bytes of local copies kept for files that are already safely on the server. */
  get keptLocalBytes() {
    return this.jobs.filter((j) => j.done).reduce((a, j) => a + (j.size ?? 0), 0);
  }

  /** Delete local copies the server has confirmed it holds in full. */
  async clearUploaded(olderThanMs = 0) {
    const cutoff = Date.now() - olderThanMs;
    const keep: UploadJob[] = [];
    for (const j of this.jobs) {
      if (j.done && (j.completedAt ?? 0) <= cutoff) {
        await this.storage.delete(j.localName).catch(() => {});
        this.forgotten.add(j.trackId);
      } else keep.push(j);
    }
    this.jobs = keep;
    this.save();
  }
}

const u32be = (b: Uint8Array, o = 0) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;

/** Walk top-level MP4 boxes from a known boundary; return the end of the last complete mdat. */
async function lastCompleteFragmentEnd(storage: LocalStorageFiles, name: string, from: number, fileSize: number) {
  let pos = from;
  let end = from;
  let moof: [number, number] | null = null;
  let lastMoof: [number, number] | null = null;
  while (pos + 8 <= fileSize) {
    const h = await storage.read(name, pos, pos + 16);
    let size = u32be(h, 0);
    if (size === 1) size = u32be(h, 8) * 2 ** 32 + u32be(h, 12);
    if (size < 8 || pos + size > fileSize) break;
    const type = String.fromCharCode(h[4], h[5], h[6], h[7]);
    if (type === 'moof') moof = [pos, pos + size];
    pos += size;
    if (type === 'moov') end = pos;
    if (type === 'mdat') {
      end = pos;
      lastMoof = moof;
    }
  }
  return { end, lastMoof };
}

/** Offset of the first audio byte in a WAV file (just past the 'data' chunk header). */
async function wavDataStart(storage: LocalStorageFiles, name: string) {
  let pos = 12;
  for (let i = 0; i < 32; i++) {
    const h = await storage.read(name, pos, pos + 8);
    if (h.length < 8) break;
    const id = String.fromCharCode(h[0], h[1], h[2], h[3]);
    if (id === 'data') return pos + 8;
    pos += 8 + (h[4] | (h[5] << 8) | (h[6] << 16) | (h[7] << 24)) + ((h[4] & 1) as number);
  }
  return 44;
}

async function sha256(data: Uint8Array) {
  const digest = await crypto.subtle.digest('SHA-256', data as Uint8Array<ArrayBuffer>);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
